import React, { FC, useState } from 'react'
import { ControllerProps, UseFormMethods, FieldErrors } from 'react-hook-form'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { Carrier, ICarrierProps } from 'apps/fleet-planner/pages/loads/Carrier'
import { ILoad, ICarrier } from 'apps/fleet-planner/types'

type IBaseControllerProps = ControllerProps<React.ComponentType<ICarrierProps>>

export interface IControlledCarrierProps
  extends Pick<IBaseControllerProps, 'control' | 'defaultValue'> {
  defaultValue?: ICarrier
  /**
   * Optional props for <Carrier /> component
   */
  CarrierProps?: Partial<ICarrierProps>
  setValue: UseFormMethods['setValue']
  errors: FieldErrors
}

/**
 * Helper component for use with React Hook Form
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledCarrier
 *   control={control}
 *   CarrierProps={{
 *     dataTest: 'motor-id-test'
 *   }} />
 * ```
 */
export const ControlledCarrier: FC<IControlledCarrierProps> = ({
  setValue,
  errors: formErrors,
  control,
  defaultValue = {},
  CarrierProps = {},
}) => {
  const { isMobile } = useBreakpointContext()
  const [carrier, setCarrier] = useState<ICarrier | undefined>(defaultValue as ICarrier)

  /**
   * Updates internal state and uses `setValue` to update
   * multiple parts of form model
   */
  const changeHandler = (change: Partial<ILoad>) => {
    const { motorCarrier: newMotorCarrier } = change || {}
    if (newMotorCarrier) {
      setValue('motorCarrierId', newMotorCarrier.id)
      setValue('motorCarrier', newMotorCarrier)
    }

    // update local state
    setCarrier(newMotorCarrier)
  }

  const {
    large = isMobile,
    refetchOptions = false,
    selectDefaultOnMount = false,
    disabled = false,
    validationMessage,
  } = CarrierProps || {}

  return (
    <>
      <Carrier
        selectDefaultOnMount={selectDefaultOnMount}
        onChange={changeHandler}
        value={{
          motorCarrierId: carrier?.id,
          motorCarrier: carrier,
        }}
        refetchOptions={refetchOptions}
        large={large}
        errors={{
          motorCarrier: {
            id: formErrors.motorCarrierId?.message,
          },
        }}
        validationMessage={validationMessage}
        disabled={disabled}
      />
      <ControlledHiddenInput name='motorCarrier' control={control} defaultValue={defaultValue} />
      <ControlledHiddenInput
        name='motorCarrierId'
        control={control}
        defaultValue={defaultValue?.motorCarrierId}
      />
    </>
  )
}
